import { useState, useEffect } from "react";
import {
  CreditCard,
  CheckCircle,
  AlertTriangle,
  Receipt,
  Calendar,
  TrendingUp,
  Loader2,
  Info,
  DollarSign,
  Clock
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useRole } from '@/contexts/RoleContext';
import { useSetupStatus } from "@/hooks/useSetupStatus";
import { SetupWarning } from "@/components/ui/setup-warning";
import { SectionInfo } from "@/components/ui/section-info";
import { VentureCard, VentureCardHeader, VentureCardTitle, VentureCardContent } from "@/components/ui/venture-card";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { format, startOfWeek, endOfWeek, addDays, differenceInDays } from "date-fns";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { PageTour } from "@/components/tour/PageTour";
import { billingSteps } from "@/lib/tour-steps";

interface PaymentMethod {
  id: string;
  last4: string;
  brand: string | null;
  exp_month: number | null;
  exp_year: number | null;
  is_primary: boolean | null;
  status: string | null;
}

interface BillingRecord {
  id: string;
  billing_week_start: string;
  billing_week_end: string;
  demos_delivered: number | null;
  billable_demos: number | null;
  demo_revenue_cents: number | null;
  revenue_share_cents: number | null;
  total_amount_cents: number;
  fanbasis_status: string | null;
  charged_at: string | null;
}

interface Client {
  id: string;
  company_name: string;
  package: string;
}

interface CurrentWeekBilling {
  currentWeekDemos: number;
  currentWeekBillableDemos: number;
  currentWeekFreeDemos: number;
  currentWeekEstimate: number;
}

export default function Billing() {
  const { clientId } = useRole();
  const setupStatus = useSetupStatus(clientId);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [billingHistory, setBillingHistory] = useState<BillingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState<Client | null>(null);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [currentWeekBilling, setCurrentWeekBilling] = useState<CurrentWeekBilling>({
    currentWeekDemos: 0,
    currentWeekBillableDemos: 0,
    currentWeekFreeDemos: 0,
    currentWeekEstimate: 0
  });
  
  useEffect(() => {
    loadClientData();
    loadBillingData();
  }, [clientId]);

  // Helper functions for week calculations
  // Billing engine uses Sun-Sat weeks — preview must match
  const getCurrentWeekStart = () => {
    const today = new Date();
    return startOfWeek(today, { weekStartsOn: 0 }); // Sunday
  };

  const getCurrentWeekEnd = () => {
    const start = getCurrentWeekStart();
    return endOfWeek(start, { weekStartsOn: 0 }); // Saturday
  };

  const getNextSunday = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysUntilSunday = dayOfWeek === 0 ? 7 : 7 - dayOfWeek;
    return addDays(today, daysUntilSunday);
  };

  const getDaysUntilSunday = () => {
    const today = new Date();
    const nextSun = getNextSunday();
    return differenceInDays(nextSun, today);
  };

  const loadClientData = async () => {
    if (!clientId) return;

    const { data, error } = await supabase
      .from('clients')
      .select('id, company_name, package')
      .eq('id', clientId)
      .single();

    if (!error && data) {
      setClient(data);
      // Calculate current week billing after client data loads
      calculateCurrentWeekBilling(clientId, data);
    }
  };

  const calculateCurrentWeekBilling = async (clientId: string, clientData: Client) => {
    const weekStart = getCurrentWeekStart();
    const weekEnd = getCurrentWeekEnd();

    try {
      // Get demos for current week
      const { data: weekDemos } = await supabase
        .from('demos')
        .select('*')
        .eq('client_id', clientId)
        .eq('status', 'completed')
        .gte('demo_date', weekStart.toISOString().split('T')[0])
        .lte('demo_date', weekEnd.toISOString().split('T')[0]);

      const currentWeekDemos = weekDemos?.length || 0;

      // Get lifetime demos to calculate free demos remaining
      const { data: lifetimeDemos } = await supabase
        .from('demos')
        .select('id')
        .eq('client_id', clientId)
        .eq('status', 'completed')
        .order('demo_date', { ascending: true });

      const lifetimeCount = lifetimeDemos?.length || 0;
      const demosBeforeThisWeek = lifetimeCount - currentWeekDemos;
      const freeRemaining = Math.max(0, 3 - demosBeforeThisWeek);
      const freeDemosThisWeek = Math.min(freeRemaining, currentWeekDemos);
      const billableDemos = Math.max(0, currentWeekDemos - freeDemosThisWeek);

      const demoPrice = clientData.package === 'elite' ? 200 : 150;
      const estimate = billableDemos * demoPrice;

      setCurrentWeekBilling({
        currentWeekDemos,
        currentWeekBillableDemos: billableDemos,
        currentWeekFreeDemos: freeDemosThisWeek,
        currentWeekEstimate: estimate
      });
    } catch (error) {
      console.error('Error calculating current week billing:', error);
    }
  };

  const loadBillingData = async () => {
    if (!clientId) {
      setLoading(false);
      return;
    }

    try {
      // Load payment methods
      const { data: methods, error: methodsError } = await supabase
        .from('payment_methods')
        .select('*')
        .eq('client_id', clientId)
        .order('is_primary', { ascending: false });

      if (!methodsError) {
        setPaymentMethods(methods || []);
      }

      // Load billing history
      const { data: billing, error: billingError } = await supabase
        .from('weekly_billing')
        .select('*')
        .eq('client_id', clientId)
        .order('billing_week_start', { ascending: false })
        .limit(20);

      if (!billingError) {
        setBillingHistory(billing || []);
      }
    } catch (error) {
      console.error("Error loading billing data:", error);
      toast.error("Failed to load billing data");
    } finally {
      setLoading(false);
    }
  };

  const handleMakePrimary = async (methodId: string) => {
    if (!clientId) return;

    // Backed by the narrow client UPDATE policy on public.payment_methods
    // added in migration 20260415000002_rls_verify_and_backfill.sql
    // (scoped via clients.user_id = auth.uid()). `.select()` + length check
    // guards against silent 0-row updates if the policy is ever dropped.
    try {
      // Set all of this client's rows to non-primary.
      const { data: clearData, error: clearError } = await supabase
        .from('payment_methods')
        .update({ is_primary: false })
        .eq('client_id', clientId)
        .select('id');

      if (clearError) {
        toast.error(clearError.message);
        return;
      }
      if (!clearData) {
        toast.error('Unable to update payment methods (no rows affected)');
        return;
      }

      // Set the selected row as primary.
      const { data: setData, error: setError } = await supabase
        .from('payment_methods')
        .update({ is_primary: true })
        .eq('id', methodId)
        .eq('client_id', clientId)
        .select('id');

      if (setError) {
        toast.error(setError.message);
        return;
      }
      if (!setData || setData.length !== 1) {
        toast.error('Could not set primary payment method — please contact support');
        return;
      }

      await loadBillingData();
      toast.success('Primary payment method updated');
    } catch (error) {
      toast.error('Failed to update primary method');
    }
  };

  const getCardIcon = (brand: string | null) => {
    const brandUpper = brand?.toUpperCase() || '';
    if (brandUpper.includes('VISA')) return '💳 VISA';
    if (brandUpper.includes('MASTER')) return '💳 MC';
    if (brandUpper.includes('AMEX')) return '💳 AMEX';
    if (brandUpper.includes('DISCOVER')) return '💳 DISC';
    return '💳';
  };

  const formatDateStr = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getDemoPrice = () => {
    return client?.package === 'elite' ? 200 : 150;
  };

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case 'succeeded':
        return <BadgeVenture variant="success"><CheckCircle className="w-3 h-3 mr-1" />Paid</BadgeVenture>;
      case 'pending':
        return <BadgeVenture variant="warning">Processing</BadgeVenture>;
      case 'failed':
        return <BadgeVenture variant="error"><AlertTriangle className="w-3 h-3 mr-1" />Failed</BadgeVenture>;
      default:
        return <BadgeVenture variant="default">Pending</BadgeVenture>;
    }
  };

  const totalDemosDelivered = billingHistory.reduce((sum, b) => sum + (b.demos_delivered || 0), 0);
  const totalBillableDemos = billingHistory.reduce((sum, b) => sum + (b.billable_demos || 0), 0);
  const totalCharged = billingHistory.reduce((sum, b) => sum + (b.total_amount_cents || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
    <PageTour steps={billingSteps} tourId="billing" />
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Billing</h1>
        <p className="text-muted-foreground">Manage your payment methods and view billing history</p>
      </div>

      {!setupStatus.loading && (
        <div className="space-y-2">
          {!setupStatus.fanbasisConnected && (
            <SetupWarning message="Billing is being set up — payment processing will be available once your account is configured." />
          )}
          {setupStatus.fanbasisConnected && setupStatus.paymentMethodCount === 0 && (
            <SetupWarning message="No payment methods on file. Contact your Venture Hills rep to add a payment method." />
          )}
          {setupStatus.fanbasisConnected && setupStatus.paymentMethodCount === 1 && (
            <SetupWarning message="Only 1 payment method on file. We recommend adding a backup to avoid missed charges." />
          )}
        </div>
      )}

      {/* Current Week Billing Preview */}
      <VentureCard data-tour-id="billing-preview" className="bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20">
        <VentureCardHeader>
          <VentureCardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" />
            This Week's Billing Preview
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p>Estimated charges based on demos delivered this week. Final amount is calculated and charged every Sunday. First 3 demos are free.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </VentureCardTitle>
          <SectionInfo description="Estimated charges for demos delivered this week. Billed every Sunday." />
        </VentureCardHeader>
        <VentureCardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Week of {format(getCurrentWeekStart(), 'MMM d')} - {format(getCurrentWeekEnd(), 'MMM d, yyyy')}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="p-4 rounded-lg bg-background/50 border border-border">
              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                <Calendar className="w-4 h-4" />
                Next Billing
              </div>
              <p className="font-semibold">{format(getNextSunday(), 'MMM d, yyyy')}</p>
            </div>

            <div className="p-4 rounded-lg bg-background/50 border border-border">
              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                <TrendingUp className="w-4 h-4 text-blue-500" />
                Demos This Week
              </div>
              <p className="font-semibold">{currentWeekBilling.currentWeekDemos}</p>
            </div>

            <div className="p-4 rounded-lg bg-background/50 border border-border">
              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                <CheckCircle className="w-4 h-4 text-purple-500" />
                Billable Demos
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Demos that will be charged at your package rate</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="flex items-center gap-2">
                <p className="font-semibold">{currentWeekBilling.currentWeekBillableDemos}</p>
                {currentWeekBilling.currentWeekFreeDemos > 0 && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-xs text-green-500 cursor-help">({currentWeekBilling.currentWeekFreeDemos} free)</span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Your first 3 demos are free — this shows how many free demos remain</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            </div>

            <div className="p-4 rounded-lg bg-background/50 border border-border">
              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                <DollarSign className="w-4 h-4 text-green-500" />
                Estimated Charge
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Projected charge for the current billing week based on completed demos</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <p className="font-semibold text-lg">${currentWeekBilling.currentWeekEstimate.toFixed(2)}</p>
            </div>
          </div>

          {/* Billing Breakdown */}
          <div className="p-4 rounded-lg bg-muted/30 border border-border">
            <h4 className="font-medium mb-3">Billing Breakdown</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Demo Rate ({client?.package === 'elite' ? 'Elite' : 'Standard'}):</span>
                <span>${getDemoPrice()}/demo</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Billable Demos This Week:</span>
                <span>{currentWeekBilling.currentWeekBillableDemos}</span>
              </div>
              {currentWeekBilling.currentWeekFreeDemos > 0 && (
                <div className="flex justify-between text-green-500">
                  <span>Free Demos Applied:</span>
                  <span>-{currentWeekBilling.currentWeekFreeDemos}</span>
                </div>
              )}
              <div className="flex justify-between font-semibold pt-2 border-t border-border">
                <span>Estimated Total:</span>
                <span>${currentWeekBilling.currentWeekEstimate.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 mt-4 text-sm text-muted-foreground">
            <Info className="w-4 h-4" />
            <span>{getDaysUntilSunday()} days remaining in billing period. Final charge may vary based on additional demos.</span>
          </div>
        </VentureCardContent>
      </VentureCard>

      {/* Payment Methods Section */}
      <VentureCard data-tour-id="payment-methods">
        <VentureCardHeader>
          <VentureCardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Payment Methods on File
          </VentureCardTitle>
        </VentureCardHeader>
        <VentureCardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            You must have at least 2 payment methods for billing redundancy
          </p>

          {paymentMethods.length === 0 ? (
            <div className="text-center py-8">
              <CreditCard className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">No payment methods on file</p>
              <p className="text-sm text-muted-foreground">Please contact support to add payment methods</p>
            </div>
          ) : (
            <div className="space-y-3">
              {paymentMethods.map((method) => (
                <div 
                  key={method.id}
                  className="flex items-center justify-between p-4 rounded-lg border border-border bg-surface"
                >
                  <div className="flex items-center gap-4">
                    <div className="text-2xl">{getCardIcon(method.brand)}</div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          {method.brand || 'Card'} •••• {method.last4}
                        </span>
                        {method.is_primary && (
                          <BadgeVenture variant="info">Primary</BadgeVenture>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Expires {String(method.exp_month).padStart(2, '0')}/{method.exp_year}
                      </p>
                      {method.status === 'expired' && (
                        <div className="flex items-center gap-1 text-destructive text-sm mt-1">
                          <AlertTriangle className="w-4 h-4" />
                          This card has expired
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!method.is_primary && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleMakePrimary(method.id)}
                      >
                        Make Primary
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setUpdateModalOpen(true)}
                    >
                      Update Card
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Info Box - Why 2 payment methods */}
          <div className="flex gap-3 p-4 rounded-lg bg-primary/10 border border-primary/20">
            <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-sm">Why 2 payment methods?</p>
              <p className="text-sm text-muted-foreground">
                We require 2 payment methods for billing redundancy. If your primary card is declined,
                we'll automatically charge your secondary card to ensure uninterrupted service.
              </p>
            </div>
          </div>

          {/* Warning if less than 2 methods */}
          {paymentMethods.length > 0 && paymentMethods.length < 2 && (
            <div className="flex gap-3 p-4 rounded-lg bg-warning/10 border border-warning/20">
              <AlertTriangle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm">
                  You only have {paymentMethods.length} payment method on file.
                  Please contact support to add a second method.
                </p>
              </div>
            </div>
          )}
        </VentureCardContent>
      </VentureCard>

      {/* Billing History Section */}
      <VentureCard data-tour-id="billing-history">
        <VentureCardHeader>
          <VentureCardTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5" />
            Billing History
          </VentureCardTitle>
          <SectionInfo description="Weekly demo charges and payment status." />
        </VentureCardHeader>
        <VentureCardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Weekly billing breakdown and payment history
          </p>

          {billingHistory.length === 0 ? (
            <div className="text-center py-12">
              <Receipt className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="font-medium mb-1">No billing history yet</p>
              <p className="text-sm text-muted-foreground">
                Your first billing will appear here after your first week of demos
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Week</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Demos Delivered</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Billable Demos</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Demo Price</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Total Charged</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Date Charged</th>
                  </tr>
                </thead>
                <tbody>
                  {billingHistory.map((bill) => {
                    const freeDemos = (bill.demos_delivered || 0) - (bill.billable_demos || 0);
                    return (
                      <tr key={bill.id} className="border-b border-border hover:bg-surface transition-colors">
                        <td className="py-3 px-4">
                          <span className="text-sm">
                            {formatDateStr(bill.billing_week_start)} - {formatDateStr(bill.billing_week_end)}
                          </span>
                        </td>
                        <td className="py-3 px-4 font-medium">{bill.demos_delivered || 0}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{bill.billable_demos || 0}</span>
                            {freeDemos > 0 && (
                              <span className="text-xs text-green-500">({freeDemos} free)</span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">${getDemoPrice()}/demo</td>
                        <td className="py-3 px-4 font-medium">
                          ${((bill.total_amount_cents || 0) / 100).toFixed(2)}
                        </td>
                        <td className="py-3 px-4">
                          {getStatusBadge(bill.fanbasis_status)}
                        </td>
                        <td className="py-3 px-4 text-sm text-muted-foreground">
                          {bill.charged_at ? formatDateStr(bill.charged_at) : '-'}
                        </td>
                      </tr>
                    );
                  })}
                  {/* Totals Row */}
                  <tr className="bg-muted/30 font-medium">
                    <td className="py-3 px-4">TOTAL</td>
                    <td className="py-3 px-4">{totalDemosDelivered}</td>
                    <td className="py-3 px-4">{totalBillableDemos}</td>
                    <td className="py-3 px-4"></td>
                    <td className="py-3 px-4">${(totalCharged / 100).toFixed(2)}</td>
                    <td className="py-3 px-4"></td>
                    <td className="py-3 px-4"></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </VentureCardContent>
      </VentureCard>

      {/* How Billing Works Info - Enhanced with Late Fee Policy */}
      <VentureCard>
        <VentureCardHeader>
          <VentureCardTitle className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-primary" />
            How Billing Works
          </VentureCardTitle>
        </VentureCardHeader>
        <VentureCardContent>
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex gap-3">
                <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm">
                    <span className="font-medium">Weekly Billing:</span>{' '}
                    We charge every Sunday at 11:59 PM for the previous week's demos
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm">
                    <span className="font-medium">First 3 Free:</span>{' '}
                    Your first 3 qualified demos are free (lifetime)
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm">
                    <span className="font-medium">Demo Pricing:</span>{' '}
                    ${getDemoPrice()} per qualified demo after the first 3
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm">
                    <span className="font-medium">Payment Methods:</span>{' '}
                    We charge your primary card first, then fallback to secondary if needed
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm">
                    <span className="font-medium">Retry Attempts:</span>{' '}
                    If both cards decline, we'll retry 3 times over 72 hours and notify you immediately
                  </p>
                </div>
              </div>
            </div>

            {/* Late Fee Policy Warning */}
            <div className="flex gap-3 p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
              <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-sm mb-2">Late Payment Policy:</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• <span className="font-medium">$50/day</span> late fee for days 1-7 after failed payment</li>
                  <li>• <span className="font-medium">Day 8:</span> Services paused until payment resolved</li>
                  <li>• <span className="font-medium">Day 15:</span> Access fully restricted until payment received</li>
                </ul>
              </div>
            </div>
          </div>
        </VentureCardContent>
      </VentureCard>

      {/* Update Payment Method Modal */}
      <Dialog open={updateModalOpen} onOpenChange={setUpdateModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Payment Method</DialogTitle>
            <DialogDescription>
              For your security, card updates are handled through our PCI-compliant payment partner.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="flex gap-3 p-4 rounded-lg bg-primary/10 border border-primary/20">
              <CreditCard className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-sm mb-1">Secure Card Management</p>
                <p className="text-sm text-muted-foreground">
                  To protect your payment information, card details are managed through the Fanbasis secure portal.
                  We never store or process raw card numbers directly.
                </p>
              </div>
            </div>

            <p className="text-sm text-muted-foreground">
              Please contact your Venture Hills account manager to update your payment method, or use the Fanbasis portal
              if you have access.
            </p>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setUpdateModalOpen(false)}
                className="flex-1"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </>
  );
}
